/**
 * Newsletter Step 1 Component
 * Email input and category selection
 */

import { useState } from 'react';
import { Mail, ChevronRight, Tv, Smartphone, Tablet, Gamepad2, Laptop, Watch, Home, Sparkles, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { dataLayerPushClickButton, dataLayerPushClickFieldTextArea } from '@/lib/ga4';

/** GA4 `Click_Field_Text_Area` form name for the newsletter sign-up modal. */
const NEWSLETTER_FORM = 'Newsletter Form';
import { useNewsletter } from '../hooks/useNewsletter';
import { MODAL_CONTENT, NEWSLETTER_CATEGORIES, VALIDATION_MESSAGES } from '../constants';
import type { NewsletterCategory } from '../types';

/** Icon configuration with colors */
const CATEGORY_ICON_CONFIG: Record<string, { icon: LucideIcon; color: string; bgColor: string }> = {
  tv: { icon: Tv, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  smartphone: { icon: Smartphone, color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
  tablet: { icon: Tablet, color: 'text-purple-600', bgColor: 'bg-purple-100' },
  gaming: { icon: Gamepad2, color: 'text-red-600', bgColor: 'bg-red-100' },
  laptop: { icon: Laptop, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  wearables: { icon: Watch, color: 'text-orange-600', bgColor: 'bg-orange-100' },
  smarthome: { icon: Home, color: 'text-teal-600', bgColor: 'bg-teal-100' },
  all: { icon: Sparkles, color: 'text-amber-600', bgColor: 'bg-amber-100' },
};

export function NewsletterStep1() {
  const { formData, updateFormData, nextStep } = useNewsletter();
  const [errors, setErrors] = useState<{ email?: string; categories?: string }>({});

  const content = MODAL_CONTENT.steps.step1;

  const validateAndNext = () => {
    const newErrors: { email?: string; categories?: string } = {};

    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = VALIDATION_MESSAGES.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = VALIDATION_MESSAGES.emailInvalid;
    }

    // Validate categories
    if (formData.categories.length === 0) {
      newErrors.categories = VALIDATION_MESSAGES.categoriesRequired;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    nextStep();
  };

  const toggleCategory = (categoryId: NewsletterCategory) => {
    const newCategories = formData.categories.includes(categoryId)
      ? formData.categories.filter((c) => c !== categoryId)
      : [...formData.categories, categoryId];
    updateFormData({ categories: newCategories });
    if (errors.categories) {
      setErrors((prev) => ({ ...prev, categories: undefined }));
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 tablet:px-6 tablet:py-6 space-y-5">
        {/* Title */}
        <div>
          <h3 className="text-base tablet:text-lg font-bold text-gray-900">
            {content.title}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{content.subtitle}</p>
        </div>

        {/* Email input */}
        <div>
          <label htmlFor="newsletter-email" className="block text-sm font-medium text-gray-700 mb-1.5">
            {content.emailLabel}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="newsletter-email"
              type="email"
              value={formData.email}
              onChange={(e) => {
                updateFormData({ email: e.target.value });
                if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
              }}
              onFocus={(e) => dataLayerPushClickFieldTextArea(e, 'Email', NEWSLETTER_FORM)}
              placeholder={content.emailPlaceholder}
              aria-required={true}
              aria-invalid={errors.email ? true : undefined}
              aria-describedby={errors.email ? 'newsletter-email-error' : undefined}
              className={cn(
                'w-full pl-10 pr-4 py-3 rounded-xl border-2 text-sm',
                'transition-all duration-200',
                'focus:outline-none focus:ring-0',
                errors.email
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-200 focus:border-[#014383] hover:border-gray-300'
              )}
            />
          </div>
          {errors.email && (
            <p id="newsletter-email-error" role="alert" className="mt-1.5 text-xs text-red-600">{errors.email}</p>
          )}
        </div>

        {/* Categories */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {content.categoriesLabel}
          </label>
          <div className="space-y-2">
            {NEWSLETTER_CATEGORIES.map((category) => {
              const isSelected = formData.categories.includes(category.id);
              const iconConfig = CATEGORY_ICON_CONFIG[category.icon];
              const IconComponent = iconConfig?.icon;
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => toggleCategory(category.id)}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left',
                    'transition-all duration-200',
                    isSelected
                      ? 'border-[#014383] bg-[#014383]/5'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  )}
                >
                  <div className={cn(
                    'w-9 h-9 rounded-lg flex items-center justify-center',
                    iconConfig?.bgColor || 'bg-gray-100'
                  )}>
                    {IconComponent && <IconComponent className={cn(
                      'w-5 h-5',
                      iconConfig?.color || 'text-gray-600'
                    )} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      'text-sm font-medium',
                      isSelected ? 'text-[#014383]' : 'text-gray-900'
                    )}>
                      {category.label}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{category.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
          {errors.categories && (
            <p role="alert" className="mt-1.5 text-xs text-red-600">{errors.categories}</p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="shrink-0 px-5 py-4 tablet:px-6 border-t border-gray-100 bg-gray-50/50">
        <button
          type="button"
          onClick={() => {
            dataLayerPushClickButton(content.nextButton, 'Next');
            validateAndNext();
          }}
          className={cn(
            'w-full px-4 py-3 rounded-xl font-bold text-sm',
            'bg-gradient-to-r from-[#014383] to-[#0159a8] text-white',
            'hover:from-[#003d7a] hover:to-[#014383]',
            'shadow-lg shadow-[#014383]/25 hover:shadow-xl',
            'active:scale-[0.98] transition-all duration-200',
            'flex items-center justify-center gap-2'
          )}
        >
          {content.nextButton}
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
